import MuiTypography from '@mui/material/Typography';
import React from 'react';

import { colors, typography } from './KestrixDesignSystem.jsx';

// ============================================================================
// REUSABLE VARIANT BUILDERS
// ============================================================================

/**
 * Creates an uppercase label variant (common pattern for labels)
 * @param {Object} options - Variant options
 * @param {string} options.color - Text color (default: colors.text.body)
 * @param {string|number} options.fontWeight - Font weight (default: typography.fontWeight.medium)
 * @param {string} options.fontSize - Font size (default: typography.fontSize.xs)
 * @param {boolean} options.block - Whether to use display: 'block' (default: true)
 * @returns {Object} Typography variant object
 */
const createUppercaseLabel = ({
  color = colors.text.body,
  fontWeight = typography.fontWeight.medium,
  fontSize = typography.fontSize.xs,
  block = true,
} = {}) => ({
  color,
  fontSize,
  fontWeight,
  textTransform: 'uppercase',
  letterSpacing: typography.letterSpacing.wide,
  ...(block && { display: 'block' }),
});

/**
 * Creates a value variant (common pattern for values/headers)
 * @param {Object} options - Variant options
 * @param {string} options.color - Text color (default: colors.text.title)
 * @param {string|number} options.fontWeight - Font weight (default: typography.fontWeight.semibold)
 * @param {string} options.fontSize - Font size (default: typography.fontSize.sm)
 * @param {string} options.letterSpacing - Letter spacing (optional)
 * @param {string} options.lineHeight - Line height (optional)
 * @returns {Object} Typography variant object
 */
const createValue = ({
  color = colors.text.title,
  fontWeight = typography.fontWeight.semibold,
  fontSize = typography.fontSize.sm,
  letterSpacing,
  lineHeight,
} = {}) => ({
  color,
  fontSize,
  fontWeight,
  ...(letterSpacing && { letterSpacing }),
  ...(lineHeight && { lineHeight }),
});

/**
 * Creates a body text variant (common pattern for helper/body text)
 * @param {Object} options - Variant options
 * @param {string} options.color - Text color (default: colors.text.body)
 * @param {string} options.fontSize - Font size (default: typography.fontSize.sm)
 * @param {string} options.fontStyle - Font style (optional, e.g., 'italic')
 * @returns {Object} Typography variant object
 */
const createBodyText = ({
  color = colors.text.body,
  fontSize = typography.fontSize.sm,
  fontStyle,
} = {}) => ({
  color,
  fontSize,
  ...(fontStyle && { fontStyle }),
});

/**
 * Creates a metric/highlighted text variant
 * @param {Object} options - Variant options
 * @param {string} options.color - Text color (default: colors.base.KestrixPrimary)
 * @param {string} options.fontSize - Font size (default: typography.fontSize.xs)
 * @param {string|number} options.fontWeight - Font weight (default: typography.fontWeight.medium)
 * @param {boolean} options.block - Whether to use display: 'block' (default: true)
 * @returns {Object} Typography variant object
 */
const createMetricText = ({
  color = colors.base.KestrixPrimary,
  fontSize = typography.fontSize.xs,
  fontWeight = typography.fontWeight.medium,
  block = true,
} = {}) => ({
  color,
  fontSize,
  fontWeight,
  ...(block && { display: 'block' }),
});

// ============================================================================
// TYPOGRAPHY VARIANTS - Using reusable builders
// ============================================================================

export const typographyVariants = {
  // Section headers (uppercase, bold, title color)
  sectionLabel: createUppercaseLabel({
    color: colors.text.title,
    fontWeight: typography.fontWeight.bold,
  }),

  // Field labels (uppercase, medium weight, body color)
  fieldLabel: createUppercaseLabel(),

  // Field values (semibold, small size, title color)
  fieldValue: createValue(),

  // Component type labels (uppercase, medium weight, body color)
  componentLabel: createUppercaseLabel(),

  // Component recommendation names (semibold, xs size, title color, tight line height)
  componentValue: createValue({
    fontSize: typography.fontSize.xs,
    lineHeight: typography.lineHeight.tight,
  }),

  // Empty state messages (italic, body color)
  emptyStateMessage: createBodyText({
    fontStyle: 'italic',
  }),

  // Headers (semibold, small, title color, tight letter spacing)
  header: createValue({
    letterSpacing: typography.letterSpacing.tight,
  }),

  // Helper/Loading text (body color, small size)
  helperText: createBodyText(),

  // Metric/Highlighted values (primary color, extra small, medium weight)
  metricText: createMetricText(),
};

// Re-export design system tokens for centralized access
export {
  borderRadius,
  colors,
  componentTokens,
  shadows,
  spacing,
  transitions,
  typography,
} from './KestrixDesignSystem.jsx';

// Re-export utility functions
export {
  getAnomalyTypeButtonStyle,
  getButtonColors,
  getSemanticColor,
  getToggleButtonStyle,
  getToolButtonStyle,
  withOpacity,
} from './KestrixDesignSystem.jsx';

/**
 * KestrixTypography - A consistent typography component using the design system
 *
 * This component provides standardized typography variants that align with the
 * Kestrix design system's typography tokens and color palette.
 *
 * @param {Object} props - Component props
 * @param {string} props.variant - Typography variant (h1-h6, body1-2, caption, overline) or predefined variant
 *                                 (sectionLabel, fieldLabel, fieldValue, componentLabel, componentValue, emptyStateMessage,
 *                                  header, helperText, metricText)
 * @param {string} [props.color] - Text color variant (title, body, subtitle, caption, disabled, primary, secondary, success, warning, error, info) (optional)
 * @param {string} [props.weight] - Font weight (light, normal, medium, semibold, bold, extrabold) (optional)
 * @param {string} [props.align] - Text alignment (left, center, right, justify) (optional)
 * @param {boolean} [props.noWrap] - Whether to prevent text wrapping (optional)
 * @param {boolean} [props.gutterBottom] - Whether to add bottom margin (optional)
 * @param {boolean} [props.fullWidth] - Whether to make the text full width (optional)
 * @param {object} [props.sx] - Additional MUI sx props (optional)
 * @param {number} [props.mb] - Bottom margin (optional)
 * @param {number} [props.mt] - Top margin (optional)
 * @param {number} [props.ml] - Left margin (optional)
 * @param {number} [props.mr] - Right margin (optional)
 * @param {number} [props.mx] - Horizontal margin (optional)
 * @param {number} [props.my] - Vertical margin (optional)
 * @param {number} [props.p] - Padding (optional)
 * @param {number} [props.px] - Horizontal padding (optional)
 * @param {number} [props.py] - Vertical padding (optional)
 * @param {React.ReactNode} props.children - Content to display (optional)
 * @returns {JSX.Element} Typography component with Kestrix design system styling
 */
const KestrixTypography = ({
  variant = 'body1',
  color = 'body',
  weight = 'normal',
  align = 'left',
  noWrap = false,
  gutterBottom = false,
  sx = {},
  children,
  ...props
}) => {
  // Check if variant is a predefined typography variant
  const predefinedVariant = typographyVariants[variant];

  // Build unified sx object: start with predefined variant, then apply computed overrides
  const getUnifiedSx = () => {
    // Start with predefined variant if it exists (contains fontSize, textTransform, display, color, fontWeight, etc.)
    const baseSx = predefinedVariant ? { ...predefinedVariant } : {};

    // Get color: use color prop if provided, otherwise use predefined variant color, or default
    const getColor = () => {
      if (predefinedVariant?.color && color === 'body') {
        return predefinedVariant.color;
      }
      switch (color) {
        case 'title':
          return colors.text.title;
        case 'body':
          return predefinedVariant?.color || colors.text.body;
        case 'subtitle':
          return colors.text.subtitle;
        case 'caption':
          return colors.text.caption;
        case 'disabled':
          return colors.text.disabled;
        case 'primary':
          return colors.primary[500];
        case 'secondary':
          return colors.secondary[500];
        case 'success':
          return colors.semantic.success.main;
        case 'warning':
          return colors.semantic.warning.main;
        case 'error':
          return colors.semantic.error.main;
        case 'info':
          return colors.semantic.info.main;
        default:
          return predefinedVariant?.color || colors.text.body;
      }
    };

    // Get font weight: use weight prop if provided, otherwise use predefined variant weight, or default
    const getFontWeight = () => {
      if (predefinedVariant?.fontWeight && weight === 'normal') {
        return predefinedVariant.fontWeight;
      }
      return (
        typography.fontWeight[weight] ||
        predefinedVariant?.fontWeight ||
        typography.fontWeight.normal
      );
    };

    // Check if variant is an actual MUI heading variant (h1-h6)
    const isHeadingVariant = /^h[1-6]$/.test(variant);

    // Get font family: always computed based on variant (not stored in predefined variants)
    const getFontFamily = () => {
      if (isHeadingVariant) {
        return typography.fontFamily.primary; // Poppins for headings
      }
      return typography.fontFamily.secondary; // Inter for body text
    };

    // Get line height: use predefined variant if available, otherwise compute based on variant
    const getLineHeight = () => {
      if (predefinedVariant?.lineHeight) {
        return predefinedVariant.lineHeight;
      }
      if (isHeadingVariant) {
        return typography.lineHeight.tight;
      }
      return typography.lineHeight.normal;
    };

    // Get letter spacing: use predefined variant if available, otherwise compute based on variant
    const getLetterSpacing = () => {
      if (predefinedVariant?.letterSpacing) {
        return predefinedVariant.letterSpacing;
      }
      if (isHeadingVariant) {
        return typography.letterSpacing.tight;
      }
      return typography.letterSpacing.normal;
    };

    // Merge: predefined variant base + computed overrides
    return {
      ...baseSx,
      fontFamily: getFontFamily(),
      fontWeight: getFontWeight(),
      lineHeight: getLineHeight(),
      letterSpacing: getLetterSpacing(),
      color: getColor(),
    };
  };

  return (
    <MuiTypography
      variant={predefinedVariant ? 'body2' : variant}
      color='inherit'
      align={align}
      noWrap={noWrap}
      gutterBottom={gutterBottom}
      sx={{
        ...getUnifiedSx(),
        ...sx,
      }}
      {...props}
    >
      {children}
    </MuiTypography>
  );
};

export default KestrixTypography;
